import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatEUR, formatDate, QUOTE_STATUS_PT, PROPOSAL_STATUS_PT, ORDER_STATUS_PT, PAYMENT_STATUS_PT, REQUEST_TYPE_PT, JOB_STATUS_PT } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  TrendingUp, ShoppingBag, FileText, Printer, Package, ArrowRight, ArrowLeft,
  AlertCircle, CheckCircle, XCircle, Plus, Trash2,
} from "lucide-react";

export function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="tech-label">a carregar…</div>;
  const k = d.kpis;

  return (
    <div className="anim-in">
      <p className="tech-label mb-2">cadditive · painel</p>
      <h1 className="text-3xl font-bold tracking-tighter">Dashboard</h1>

      {/* Integration alerts */}
      <div className="mt-6 space-y-2">
        {!d.integrations.email && <AlertBox variant="warning" text="Emails desativados: EMERGENT_EMAIL_KEY não configurado." />}
        {!d.integrations.whatsapp && <AlertBox variant="info" text="Notificações WhatsApp ainda não configuradas — as notificações ficam registadas como pendentes." />}
        {!d.integrations.storage && <AlertBox variant="warning" text="Armazenamento de ficheiros indisponível." />}
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Kpi icon={FileText} label="Novos pedidos" value={k.quotes_new} to="/admin/orcamentos?status=new" />
        <Kpi icon={FileText} label="Em análise" value={k.quotes_review} to="/admin/orcamentos" />
        <Kpi icon={FileText} label="Propostas enviadas" value={k.proposals_sent} to="/admin/propostas" />
        <Kpi icon={CheckCircle} label="Propostas aceites" value={k.proposals_accepted} to="/admin/propostas" />
        <Kpi icon={ShoppingBag} label="Novas encomendas" value={k.orders_new} to="/admin/encomendas" />
        <Kpi icon={Package} label="Em produção" value={k.orders_prod} to="/admin/encomendas" />
        <Kpi icon={Printer} label="Impressoras livres" value={k.printers_avail} to="/admin/impressoras" />
        <Kpi icon={TrendingUp} label="Receita 30d" value={formatEUR(k.revenue_month)} to="/admin/encomendas" />
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <Card title="Trabalhos de impressão">
          <div className="flex gap-6">
            <Metric label="Em impressão" v={k.jobs_printing} />
            <Metric label="Em fila" v={k.jobs_queue} />
            <Metric label="A imprimir" v={k.printers_printing} />
          </div>
        </Card>
        <Card title="Ações rápidas">
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/orcamentos" className="btn-outline text-xs">Ver orçamentos</Link>
            <Link to="/admin/producao" className="btn-outline text-xs">Abrir produção</Link>
            <Link to="/admin/produtos" className="btn-outline text-xs">Gerir produtos</Link>
            <Link to="/admin/impressoras" className="btn-outline text-xs">Impressoras</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, to }) => (
  <Link to={to} className="border border-border p-5 bg-card hover:border-foreground">
    <div className="flex items-start justify-between"><Icon size={18} className="text-brand" /><ArrowRight size={12} className="text-muted-foreground" /></div>
    <div className="text-3xl font-bold mt-4">{value}</div>
    <div className="tech-label mt-1">{label}</div>
  </Link>
);
const Metric = ({ label, v }) => <div><div className="text-2xl font-bold">{v}</div><div className="tech-label mt-1">{label}</div></div>;
const Card = ({ title, children }) => <div className="border border-border p-5 bg-card"><p className="tech-label mb-3">{title}</p>{children}</div>;
const AlertBox = ({ variant, text }) => (
  <div className={`border p-3 text-xs flex items-center gap-2 ${variant === "warning" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-blue-200 bg-blue-50 text-blue-800"}`}>
    <AlertCircle size={14} /> {text}
  </div>
);

// ---- Quotes (admin) ----
export function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/quotes").then((r) => setItems(r.data)); }, []);
  const filtered = items.filter((i) =>
    !q || i.code?.toLowerCase().includes(q.toLowerCase()) || i.title?.toLowerCase().includes(q.toLowerCase()) ||
    i.customer?.name?.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter">Pedidos de orçamento</h1>
      <input className="input mt-6 max-w-sm" placeholder="Procurar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-quotes-search" />
      <div className="border border-border overflow-hidden mt-4">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Ref.</Th><Th>Cliente</Th><Th>Tipo</Th><Th>Estado</Th><Th>Data</Th><Th /></tr></thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.quote_id} className="border-t border-border hover:bg-secondary">
                <Td className="mono">{i.code}</Td>
                <Td>{i.customer?.name || "—"}</Td>
                <Td className="text-xs">{REQUEST_TYPE_PT[i.request_type]}</Td>
                <Td><StatusBadge status={i.status} label={QUOTE_STATUS_PT[i.status]} /></Td>
                <Td>{formatDate(i.created_at)}</Td>
                <Td><Link to={`/admin/orcamentos/${i.quote_id}`} className="text-brand link-underline">Abrir</Link></Td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 tech-label text-center">sem pedidos</td></tr>}
          </tbody>
        </table>
      </div>
      <style>{ADMIN_INPUT_CSS}</style>
    </div>
  );
}

export function AdminQuoteDetail() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("");
  const nav = useNavigate();
  const reload = () => {
    api.get(`/quotes/${id}`).then((r) => { setQ(r.data); setStatus(r.data.status); });
    api.get(`/files/by-quote/${id}`).then((r) => setFiles(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, [id]);

  async function updateStatus(newStatus) {
    try { await api.patch(`/quotes/${id}`, { status: newStatus }); toast.success("Estado atualizado"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  }

  // Proposal creation state
  const [lines, setLines] = useState([{ description: "Modelação e impressão", quantity: 1, unit_price: 0 }]);
  const subtotal = lines.reduce((s, l) => s + (l.unit_price * l.quantity), 0);
  const vat = Math.round(subtotal * 0.23 * 100) / 100;
  const total = subtotal + vat;
  const [shipping, setShipping] = useState(0);
  const [leadTime, setLeadTime] = useState(7);
  const [obs, setObs] = useState("");

  async function createProposal() {
    try {
      const payload = {
        quote_id: id,
        lines: lines.map((l) => ({ ...l, total: l.unit_price * l.quantity })),
        subtotal, vat_rate: 23, vat_amount: vat, shipping,
        total: total + shipping, lead_time_days: leadTime,
        payment_terms: "50% adiantado, restante antes de expedição",
        validity_days: 15, terms: "", observations: obs,
      };
      const { data } = await api.post("/proposals", payload);
      await api.post(`/proposals/${data.proposal_id}/send`);
      toast.success(`Proposta ${data.code} enviada`);
      nav("/admin/propostas");
    } catch (e) { toast.error(apiError(e)); }
  }

  const download = async (fid, name) => {
    const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };

  if (!q) return <div className="tech-label">a carregar…</div>;
  return (
    <div className="anim-in">
      <Link to="/admin/orcamentos" className="text-xs inline-flex items-center gap-1 text-muted-foreground mb-4"><ArrowLeft size={12} /> Voltar</Link>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><p className="tech-label">{q.code}</p><h1 className="text-3xl font-bold tracking-tighter">{q.title}</h1></div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => { setStatus(e.target.value); updateStatus(e.target.value); }} className="input" data-testid="admin-quote-status">
            {Object.entries(QUOTE_STATUS_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Detalhes">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Info k="Cliente" v={q.customer?.name} />
              <Info k="Email" v={q.customer?.email} />
              <Info k="Telefone" v={q.customer?.phone || "—"} />
              <Info k="Empresa" v={q.customer?.company || "—"} />
              <Info k="NIF" v={q.customer?.vat_number || "—"} />
              <Info k="Localidade" v={q.customer?.locality || "—"} />
              <Info k="Tipo" v={REQUEST_TYPE_PT[q.request_type]} />
              <Info k="Quantidade" v={q.quantity} />
              <Info k="Dimensões" v={q.dimensions || "—"} />
              <Info k="Prazo" v={q.deadline ? formatDate(q.deadline) : "—"} />
              <Info k="Material pref." v={q.material_pref || "a recomendar"} />
              <Info k="Cor" v={q.color_pref || "—"} />
            </div>
            <div className="mt-4"><p className="tech-label mb-2">Descrição</p><p className="text-sm whitespace-pre-line">{q.description}</p></div>
            {Object.keys(q.requirements || {}).length > 0 && (
              <div className="mt-4"><p className="tech-label mb-2">Requisitos técnicos</p>
                <div className="flex flex-wrap gap-2">{Object.entries(q.requirements).filter(([_, v]) => v).map(([k]) => <span key={k} className="mono text-[10px] uppercase px-2 py-1 border border-border">{k}</span>)}</div>
              </div>
            )}
          </Section>
          <Section title="Ficheiros">
            {files.length === 0 && <p className="tech-label">sem ficheiros</p>}
            {files.map((f) => (
              <div key={f.file_id} className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm">{f.original_filename}</span>
                <button onClick={() => download(f.file_id, f.original_filename)} className="text-brand text-xs link-underline">Descarregar</button>
              </div>
            ))}
          </Section>

          <Section title="Criar proposta">
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input className="input col-span-6" value={l.description} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} placeholder="Descrição" />
                  <input type="number" min={1} className="input col-span-2" value={l.quantity} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, quantity: Number(e.target.value) } : x))} />
                  <input type="number" step="0.01" className="input col-span-3" value={l.unit_price} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value) } : x))} placeholder="€/un" />
                  <button onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                </div>
              ))}
              <button onClick={() => setLines((l) => [...l, { description: "", quantity: 1, unit_price: 0 }])} className="btn-outline text-xs"><Plus size={12} /> Adicionar linha</button>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
              <div><label className="tech-label">Portes (€)</label><input type="number" step="0.01" className="input" value={shipping} onChange={(e) => setShipping(Number(e.target.value))} /></div>
              <div><label className="tech-label">Prazo (dias)</label><input type="number" className="input" value={leadTime} onChange={(e) => setLeadTime(Number(e.target.value))} /></div>
            </div>
            <div className="mt-3"><label className="tech-label">Observações</label><textarea className="input" rows={3} value={obs} onChange={(e) => setObs(e.target.value)} /></div>
            <div className="mt-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatEUR(subtotal)}</span></div>
              <div className="flex justify-between"><span>IVA 23%</span><span>{formatEUR(vat)}</span></div>
              <div className="flex justify-between font-bold border-t border-border pt-2 mt-2"><span>Total</span><span>{formatEUR(total + shipping)}</span></div>
            </div>
            <button onClick={createProposal} className="btn-primary mt-4" data-testid="admin-create-proposal">Criar e enviar proposta</button>
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Histórico">
            {(q.history || []).map((h, i) => (
              <div key={i} className="flex justify-between text-xs border-b border-border py-1.5">
                <span>{h.status}</span><span className="mono">{formatDate(h.at)}</span>
              </div>
            ))}
          </Section>
        </div>
      </div>
      <style>{ADMIN_INPUT_CSS}</style>
    </div>
  );
}

// ---- Proposals ----
export function AdminProposals() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/proposals").then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Propostas</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Ref.</Th><Th>Cliente</Th><Th>Total</Th><Th>Estado</Th><Th>Data</Th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.proposal_id} className="border-t border-border">
                <Td className="mono">{p.code}</Td>
                <Td>{p.customer?.name}</Td>
                <Td className="font-medium">{formatEUR(p.total)}</Td>
                <Td><StatusBadge status={p.status} label={PROPOSAL_STATUS_PT[p.status]} /></Td>
                <Td>{formatDate(p.created_at)}</Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center tech-label">sem propostas</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Orders ----
export function AdminOrders() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/orders").then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Encomendas</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Ref.</Th><Th>Cliente</Th><Th>Total</Th><Th>Estado</Th><Th>Pagamento</Th><Th>Data</Th><Th /></tr></thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.order_id} className="border-t border-border">
                <Td className="mono">{o.code}</Td>
                <Td>{o.customer?.name}</Td>
                <Td className="font-medium">{formatEUR(o.total)}</Td>
                <Td><StatusBadge status={o.status} label={ORDER_STATUS_PT[o.status]} /></Td>
                <Td><StatusBadge status={o.payment_status} label={PAYMENT_STATUS_PT[o.payment_status]} /></Td>
                <Td>{formatDate(o.created_at)}</Td>
                <Td><Link className="text-brand link-underline" to={`/admin/encomendas/${o.order_id}`}>Abrir</Link></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center tech-label">sem encomendas</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [carrier, setCarrier] = useState("CTT");
  const [tracking, setTracking] = useState("");
  const reload = () => api.get(`/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { reload(); }, [id]);

  async function changeStatus(field, value) {
    await api.patch(`/orders/${id}`, { [field]: value });
    toast.success("Atualizado"); reload();
  }
  async function updateLine(line_id, patch) {
    await api.patch(`/orders/${id}/lines/${line_id}`, patch);
    toast.success("Linha atualizada"); reload();
  }
  async function ship() {
    if (!tracking) return toast.error("Introduza código de rastreamento");
    try { await api.post(`/orders/${id}/ship`, { carrier, tracking_code: tracking }); toast.success("Expedição registada"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  }

  if (!o) return <div className="tech-label">a carregar…</div>;
  return (
    <div className="anim-in">
      <Link to="/admin/encomendas" className="text-xs inline-flex items-center gap-1 text-muted-foreground mb-4"><ArrowLeft size={12} /> Voltar</Link>
      <p className="tech-label">{o.code} · {o.customer?.name}</p>
      <div className="flex flex-wrap items-center gap-3 mt-1">
        <h1 className="text-3xl font-bold tracking-tighter">Encomenda</h1>
        <StatusBadge status={o.status} label={ORDER_STATUS_PT[o.status]} />
        <StatusBadge status={o.payment_status} label={PAYMENT_STATUS_PT[o.payment_status]} />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Linhas / Produção">
            {o.lines.map((l) => (
              <div key={l.line_id} className="border border-border p-4 mb-2">
                <div className="flex justify-between mb-2">
                  <div><div className="font-medium">{l.name} × {l.quantity}</div>
                    <div className="mono text-[10px] uppercase text-muted-foreground">{Object.entries(l.variant || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                  </div>
                  <div className="text-right font-medium">{formatEUR(l.unit_price * l.quantity)}</div>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <select className="input" value={l.status || "waiting_planning"} onChange={(e) => updateLine(l.line_id, { status: e.target.value })} data-testid={`admin-line-status-${l.line_id}`}>
                    {["waiting_planning","in_queue","in_printing","printing_paused","printing_done","quality_check","reprint","in_finishing","in_assembly","in_packaging","ready_shipping","shipped","problem","completed"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </Section>

          <Section title="Expedição">
            {o.shipment ? (
              <div className="text-sm">
                <p><b>{o.shipment.carrier}</b> · Código <span className="mono">{o.shipment.tracking_code}</span></p>
                <p className="text-muted-foreground text-xs">{formatDate(o.shipment.shipped_at)}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-2">
                <input className="input" placeholder="Transportadora" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
                <input className="input" placeholder="Código de rastreamento" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="admin-ship-tracking" />
                <button className="btn-primary text-xs" onClick={ship} data-testid="admin-ship-btn">Registar expedição</button>
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Ações">
            <label className="tech-label">Estado comercial</label>
            <select value={o.status} onChange={(e) => changeStatus("status", e.target.value)} className="input mt-1 mb-3" data-testid="admin-order-status">
              {Object.entries(ORDER_STATUS_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="tech-label">Pagamento</label>
            <select value={o.payment_status} onChange={(e) => changeStatus("payment_status", e.target.value)} className="input mt-1" data-testid="admin-order-payment">
              {Object.entries(PAYMENT_STATUS_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Section>
          <Section title="Resumo">
            <Info k="Subtotal" v={formatEUR(o.subtotal)} />
            <Info k="IVA" v={formatEUR(o.vat_amount)} />
            <Info k="Portes" v={formatEUR(o.shipping_cost)} />
            <div className="flex justify-between font-bold border-t border-border pt-2 mt-2"><span>Total</span><span>{formatEUR(o.total)}</span></div>
          </Section>
        </div>
      </div>
      <style>{ADMIN_INPUT_CSS}</style>
    </div>
  );
}

// ---- Products ----
export function AdminProducts() {
  const [items, setItems] = useState([]);
  const reload = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);
  return (
    <div className="anim-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tighter">Produtos</h1>
        <Link to="/admin/produtos/novo" className="btn-primary text-xs"><Plus size={12} /> Novo produto</Link>
      </div>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>SKU</Th><Th>Nome</Th><Th>Categoria</Th><Th>Preço</Th><Th>Stock</Th><Th /></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.product_id} className="border-t border-border">
                <Td className="mono">{p.sku}</Td>
                <Td>{p.name}</Td>
                <Td className="text-xs text-muted-foreground">{p.category}</Td>
                <Td className="font-medium">{formatEUR(p.price)}</Td>
                <Td className="mono">{p.stock}</Td>
                <Td><Link to={`/admin/produtos/${p.product_id}`} className="text-brand link-underline">Editar</Link></Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center tech-label">sem produtos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminProductForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "novo";
  const [p, setP] = useState({
    name: "", sku: "", category: "surdinas", short_description: "", description: "",
    price: 0, promo_price: null, vat_rate: 23, material: "", color: "",
    dimensions: "", weight_g: 0, lead_time_days: 5, stock: 0, made_to_order: true,
    images: [], variants: [], custom_options: [], active: true, featured: false,
  });
  useEffect(() => {
    if (!isNew) api.get(`/products/${id}`).then((r) => setP(r.data));
  }, [id, isNew]);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  async function save() {
    try {
      if (isNew) {
        await api.post("/products", p);
        toast.success("Produto criado");
      } else {
        await api.patch(`/products/${id}`, p);
        toast.success("Produto atualizado");
      }
      nav("/admin/produtos");
    } catch (e) { toast.error(apiError(e)); }
  }
  return (
    <div className="anim-in max-w-3xl">
      <Link to="/admin/produtos" className="text-xs inline-flex items-center gap-1 text-muted-foreground mb-4"><ArrowLeft size={12} /> Voltar</Link>
      <h1 className="text-3xl font-bold tracking-tighter">{isNew ? "Novo produto" : "Editar produto"}</h1>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <F label="Nome"><input className="input" value={p.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name" /></F>
        <F label="SKU"><input className="input mono" value={p.sku} onChange={(e) => set("sku", e.target.value)} data-testid="product-sku" /></F>
        <F label="Categoria"><input className="input" value={p.category} onChange={(e) => set("category", e.target.value)} /></F>
        <F label="Preço (€)"><input type="number" step="0.01" className="input" value={p.price} onChange={(e) => set("price", Number(e.target.value))} data-testid="product-price" /></F>
        <F label="Material"><input className="input" value={p.material} onChange={(e) => set("material", e.target.value)} /></F>
        <F label="Cor"><input className="input" value={p.color} onChange={(e) => set("color", e.target.value)} /></F>
        <F label="Prazo (dias)"><input type="number" className="input" value={p.lead_time_days} onChange={(e) => set("lead_time_days", Number(e.target.value))} /></F>
        <F label="Stock"><input type="number" className="input" value={p.stock} onChange={(e) => set("stock", Number(e.target.value))} /></F>
        <F label="Descrição curta" className="sm:col-span-2"><input className="input" value={p.short_description} onChange={(e) => set("short_description", e.target.value)} /></F>
        <F label="Descrição" className="sm:col-span-2"><textarea rows={4} className="input" value={p.description} onChange={(e) => set("description", e.target.value)} /></F>
        <F label="Imagem URL" className="sm:col-span-2">
          <input className="input" placeholder="https://…" value={p.images?.[0] || ""} onChange={(e) => set("images", [e.target.value])} />
        </F>
        <F label="Destaque"><label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} /> Ativo</label></F>
        <F label="Produção por encomenda"><label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={p.made_to_order} onChange={(e) => set("made_to_order", e.target.checked)} /> Sim</label></F>
      </div>
      <button onClick={save} className="btn-primary mt-8" data-testid="product-save">Guardar</button>
      <style>{ADMIN_INPUT_CSS}</style>
    </div>
  );
}

// ---- Printers / Materials / Production kanban ----
export function AdminPrinters() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [p, setP] = useState({ name: "", brand: "Bambu Lab", model: "", serial_number: "", location: "Barcelos", nozzle_mm: 0.4, hourly_cost: 3.5, status: "available" });
  const reload = () => api.get("/production/printers").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);
  async function create() {
    try { await api.post("/production/printers", p); toast.success("Impressora criada"); reload(); setShow(false); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function updateStatus(id, status) { await api.patch(`/production/printers/${id}`, { status }); reload(); }
  return (
    <div className="anim-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tighter">Impressoras</h1>
        <button onClick={() => setShow(true)} className="btn-primary text-xs" data-testid="admin-add-printer"><Plus size={12} /> Nova impressora</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((pr) => (
          <div key={pr.printer_id} className="border border-border p-5 bg-card">
            <div className="flex items-start justify-between">
              <div><p className="tech-label">{pr.brand}</p><h3 className="font-bold text-lg tracking-tight">{pr.name}</h3></div>
              <StatusBadge status={pr.status} label={pr.status} />
            </div>
            <div className="mt-3 text-sm space-y-1"><Row k="Nozzle" v={`${pr.nozzle_mm} mm`} /><Row k="Volume" v={pr.build_volume || "—"} /><Row k="Custo/h" v={formatEUR(pr.hourly_cost)} /></div>
            <select className="input mt-3" value={pr.status} onChange={(e) => updateStatus(pr.printer_id, e.target.value)}>
              {["available","printing","reserved","maintenance","offline"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
      </div>
      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShow(false)}>
          <div className="bg-card border border-border p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Nova impressora</h2>
            <div className="space-y-3">
              <input className="input" placeholder="Nome" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} />
              <input className="input" placeholder="Marca" value={p.brand} onChange={(e) => setP({ ...p, brand: e.target.value })} />
              <input className="input" placeholder="Modelo" value={p.model} onChange={(e) => setP({ ...p, model: e.target.value })} />
              <input className="input" placeholder="Número de série" value={p.serial_number} onChange={(e) => setP({ ...p, serial_number: e.target.value })} />
              <input className="input" type="number" step="0.1" placeholder="Custo/hora" value={p.hourly_cost} onChange={(e) => setP({ ...p, hourly_cost: Number(e.target.value) })} />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={create} className="btn-primary text-sm">Criar</button>
              <button onClick={() => setShow(false)} className="btn-outline text-sm">Cancelar</button>
            </div>
          </div>
        </div>
      )}
      <style>{ADMIN_INPUT_CSS}</style>
    </div>
  );
}

export function AdminMaterials() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [m, setM] = useState({ name: "", type: "PLA", brand: "", color: "", weight_initial_g: 1000, weight_available_g: 1000, cost_per_kg: 25 });
  const reload = () => api.get("/production/materials").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);
  async function create() {
    try { await api.post("/production/materials", m); toast.success("Material criado"); reload(); setShow(false); }
    catch (e) { toast.error(apiError(e)); }
  }
  return (
    <div className="anim-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tighter">Materiais</h1>
        <button onClick={() => setShow(true)} className="btn-primary text-xs" data-testid="admin-add-material"><Plus size={12} /> Novo material</button>
      </div>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Nome</Th><Th>Tipo</Th><Th>Marca</Th><Th>Cor</Th><Th>Disponível</Th><Th>€/kg</Th></tr></thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.material_id} className="border-t border-border">
                <Td>{m.name}</Td><Td className="mono">{m.type}</Td><Td>{m.brand}</Td><Td>{m.color}</Td>
                <Td className="mono">{m.weight_available_g}g / {m.weight_initial_g}g</Td>
                <Td className="mono">{formatEUR(m.cost_per_kg)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <div className="bg-card border border-border p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Novo material</h2>
            <div className="grid grid-cols-2 gap-3">
              <input className="input" placeholder="Nome" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} />
              <select className="input" value={m.type} onChange={(e) => setM({ ...m, type: e.target.value })}>{["PLA","PETG","PETG-CF","ASA","ABS","TPU","PA-CF"].map(t => <option key={t}>{t}</option>)}</select>
              <input className="input" placeholder="Marca" value={m.brand} onChange={(e) => setM({ ...m, brand: e.target.value })} />
              <input className="input" placeholder="Cor" value={m.color} onChange={(e) => setM({ ...m, color: e.target.value })} />
              <input type="number" className="input" placeholder="Peso inicial (g)" value={m.weight_initial_g} onChange={(e) => setM({ ...m, weight_initial_g: Number(e.target.value), weight_available_g: Number(e.target.value) })} />
              <input type="number" step="0.01" className="input" placeholder="Custo/kg" value={m.cost_per_kg} onChange={(e) => setM({ ...m, cost_per_kg: Number(e.target.value) })} />
            </div>
            <div className="flex gap-2 mt-4"><button onClick={create} className="btn-primary text-sm">Criar</button><button onClick={() => setShow(false)} className="btn-outline text-sm">Cancelar</button></div>
          </div>
        </div>
      )}
      <style>{ADMIN_INPUT_CSS}</style>
    </div>
  );
}

// ---- Production Kanban ----
export function AdminProduction() {
  const [jobs, setJobs] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [j, setJ] = useState({ product_name: "", quantity: 1, priority: "normal" });
  const reload = () => {
    api.get("/production/jobs").then((r) => setJobs(r.data));
    api.get("/production/printers").then((r) => setPrinters(r.data));
  };
  useEffect(() => { reload(); }, []);

  async function createJob() {
    try { await api.post("/production/jobs", j); toast.success("Trabalho criado"); setShowNew(false); reload(); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function updateJob(id, patch) {
    await api.patch(`/production/jobs/${id}`, patch); reload();
  }

  const cols = ["queued", "printing", "paused", "completed", "failed"];
  const grouped = Object.fromEntries(cols.map((c) => [c, jobs.filter((j) => j.status === c)]));

  function onDrop(e, status) {
    const jid = e.dataTransfer.getData("text/plain");
    if (jid) updateJob(jid, { status });
  }

  return (
    <div className="anim-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tighter">Produção · Kanban</h1>
        <button onClick={() => setShowNew(true)} className="btn-primary text-xs" data-testid="admin-add-job"><Plus size={12} /> Novo trabalho</button>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        {printers.map((pr) => (
          <div key={pr.printer_id} className="border border-border p-3 bg-card text-sm">
            <div className="flex justify-between items-center"><span className="font-medium">{pr.name}</span><StatusBadge status={pr.status} label={pr.status} /></div>
            <p className="mono text-[10px] text-muted-foreground uppercase mt-1">{pr.location}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4 -mx-6 px-6 lg:mx-0 lg:px-0" data-testid="production-kanban">
        {cols.map((c) => (
          <div key={c} className="kanban-col p-3 rounded shrink-0" onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, c)}>
            <div className="flex justify-between items-center mb-3">
              <span className="tech-label">{JOB_STATUS_PT[c]}</span>
              <span className="mono text-xs text-muted-foreground">{grouped[c].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[c].map((j) => (
                <div key={j.job_id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", j.job_id)}
                  className="border border-border p-3 bg-background cursor-grab active:cursor-grabbing hover:border-foreground">
                  <p className="mono text-[10px] uppercase text-muted-foreground">{j.job_id.slice(-6)}</p>
                  <p className="font-medium text-sm mt-1">{j.product_name}</p>
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="text-muted-foreground">×{j.quantity}</span>
                    <span className="mono text-[10px]">{j.priority}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-card border border-border p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Novo trabalho</h2>
            <input className="input mb-2" placeholder="Nome do produto/peça" value={j.product_name} onChange={(e) => setJ({ ...j, product_name: e.target.value })} />
            <input type="number" className="input mb-2" placeholder="Quantidade" value={j.quantity} onChange={(e) => setJ({ ...j, quantity: Number(e.target.value) })} />
            <select className="input mb-2" value={j.priority} onChange={(e) => setJ({ ...j, priority: e.target.value })}>
              <option value="low">Baixa</option><option value="normal">Normal</option><option value="high">Alta</option>
            </select>
            <div className="flex gap-2 mt-4"><button onClick={createJob} className="btn-primary text-sm">Criar</button><button onClick={() => setShowNew(false)} className="btn-outline text-sm">Cancelar</button></div>
          </div>
        </div>
      )}
      <style>{ADMIN_INPUT_CSS}</style>
    </div>
  );
}

export function AdminCustomers() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/users", { params: { role: "customer" } }).then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Clientes</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Nome</Th><Th>Email</Th><Th>Tipo</Th><Th>Empresa</Th><Th>Criado</Th></tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-border">
                <Td>{u.name}</Td><Td>{u.email}</Td><Td className="text-xs">{u.customer_type}</Td>
                <Td className="text-xs">{u.company?.name || "—"}</Td>
                <Td>{formatDate(u.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminNotifications() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/notifications").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Notificações</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Canal</Th><Th>Para</Th><Th>Assunto</Th><Th>Estado</Th><Th>Data</Th></tr></thead>
          <tbody>
            {items.map((n, i) => (
              <tr key={i} className="border-t border-border">
                <Td className="mono">{n.channel}</Td><Td className="text-xs">{n.to}</Td><Td>{n.subject}</Td>
                <Td><StatusBadge status={n.status} label={n.status} /></Td>
                <Td>{formatDate(n.created_at)}</Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 tech-label text-center">sem notificações</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminSettings() {
  return (
    <div className="anim-in max-w-3xl">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Definições</h1>
      <div className="border border-border p-6 bg-card space-y-3 text-sm">
        <p className="tech-label">Marca</p>
        <Info k="Nome" v="cadditive" />
        <Info k="Sede" v="Barcelos, Portugal" />
        <Info k="Contacto" v="+351 912 000 000 · geral@cadditive.pt" />
        <p className="tech-label mt-6">IVA</p>
        <Info k="Taxa aplicável" v="23%" />
        <p className="tech-label mt-6">Integrações</p>
        <p className="text-xs text-muted-foreground">Configuráveis via variáveis de ambiente (EMERGENT_EMAIL_KEY, EMERGENT_LLM_KEY, WhatsApp).</p>
      </div>
    </div>
  );
}

export function AdminProjects() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setItems(r.data)); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Projetos</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Ref.</Th><Th>Título</Th><Th>Cliente</Th><Th>Estado</Th><Th>Data</Th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.project_id} className="border-t border-border">
                <Td className="mono">{p.code}</Td><Td>{p.title}</Td><Td>{p.customer?.name}</Td>
                <Td><StatusBadge status={p.status} label={p.status} /></Td>
                <Td>{formatDate(p.created_at)}</Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 tech-label text-center">sem projetos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminShipments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/orders").then((r) => setItems(r.data.filter((o) => o.shipment))); }, []);
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-6">Expedições</h1>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left"><Th>Encomenda</Th><Th>Cliente</Th><Th>Transportadora</Th><Th>Rastreamento</Th><Th>Data</Th></tr></thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.order_id} className="border-t border-border">
                <Td className="mono"><Link className="link-underline text-brand" to={`/admin/encomendas/${o.order_id}`}>{o.code}</Link></Td>
                <Td>{o.customer?.name}</Td>
                <Td>{o.shipment?.carrier}</Td>
                <Td className="mono">{o.shipment?.tracking_code}</Td>
                <Td>{formatDate(o.shipment?.shipped_at)}</Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 tech-label text-center">sem expedições</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Shared subcomponents
const Th = ({ children, className = "" }) => <th className={`px-4 py-3 tech-label ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Section = ({ title, children }) => (<div className="border border-border p-5 bg-card"><p className="tech-label mb-3">{title}</p>{children}</div>);
const Row = ({ k, v }) => (<div className="flex justify-between text-sm py-1"><span className="tech-label">{k}</span><span className="font-medium">{v}</span></div>);
const Info = ({ k, v }) => (<div className="flex justify-between text-sm py-1"><span className="tech-label">{k}</span><span className="font-medium">{v}</span></div>);
const F = ({ label, children, className = "" }) => (<label className={`block ${className}`}><span className="tech-label block mb-2">{label}</span>{children}</label>);
const ADMIN_INPUT_CSS = `.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:.5rem .75rem;font-size:.875rem;outline:none;transition:border-color .15s}.input:focus{border-color:var(--brand)}`;
